/** Shared query key for GET /v1/time-attendance/workplaces/:id so all setup steps use one cache (single fetch). */
export const SETUP_WORKPLACE_QUERY_KEY = "time-attendance-setup-progress"

/** Local subset of workplace config flags used to determine which setup step to resume from. */
export type IWorkplaceConfigResume = Pick<
  Record<string, unknown>,
  | "workplaceSelected"
  | "dutyLocationSelected"
  | "shiftPlanConfigured"
  | "shiftPermissionsConfigured"
  | "shiftPeriodConfigured"
  | "shiftEmployeesConfigured"
  | "shiftPlansConfirmed"
  | "configDone"
  | "status"
>

/** Payload for updating an existing workplace (office address) in Setup step. */
export interface UpdateWorkplaceBody {
  office_address_id: string
  edit_office_address_line_1: string
  edit_office_address_line_2: string
  edit_office_address_city: string
  edit_office_address_state_code: string
  edit_office_address_zip_code: string
  edit_office_address_country_code: string
  edit_office_address_phone: string
  edit_display_name: string
  edit_tax_office_name: string
  edit_tax_office_code: string
  edit_housing_location_name: string
  edit_housing_location_code: string
  edit_latitude: string
  edit_longitude: string
  au_comment: string
}

/** Payload for creating a new workplace (office address) in Setup step. */
export interface CreateWorkplaceBody {
  office_address_line_1: string
  office_address_state_code: string
  display_name: string
  tax_office_name: string
  tax_office_code: string
  housing_location_name: string
  housing_location_code: string
  latitude: string
  longitude: string
}

/** Response for GET /v1/time-attendance/workplaces (paginated docs) */
export type IWorkplaceRes = IBaseRes<IWorkplacesData>

export interface IWorkplacesData {
  docs: IWorkplaceDoc[]
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

export interface IWorkplaceConfig {
  id: number
  companyId: number
  workplaceId: number
  isActive: boolean
  status: string
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  configDone: boolean
  workplaceSelected: boolean
  dutyLocationSelected?: boolean
  shiftPlanConfigured: boolean
  shiftPermissionsConfigured: boolean
  shiftPeriodConfigured: boolean
  shiftEmployeesConfigured: boolean
  shiftPlansConfirmed: boolean
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  createdByName: string
  updatedByName: string
}

export interface IWorkplaceDoc {
  id: number
  addressLine1: string
  addressLine2: string | null
  addressCity: string
  addressState: string
  stateCode: string
  zipCode: string
  isPrimary: boolean | null
  phone: string
  displayName: string
  taxOfficeName: string | null
  taxOfficeCode: string | null
  taxId: string | null
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  country: string
  countryCode: string
  companyId: number
  housingLocationName: string | null
  housingLocationCode: string | null
  fullAddress: string
  hasChildren: boolean
  longitude: string | null
  latitude: string | null
  workplaceConfig?: IWorkplaceConfig
  shiftPlans: unknown[]
  shiftPlanHardwareRequests: unknown[]
  officeDutyLocations: unknown[]
  shiftAssignments: unknown[]
  shiftPeriods: unknown[]
  eligibilityPools: unknown[]
  timeSheets: unknown[]
}

/** Response for GET /v1/time-attendance/workplaces/:id/duty-locations (paginated) */
export type IDutyLocationsRes = IBaseRes<IDutyLocationsData>

export interface IDutyLocationsData {
  docs: IDutyLocationDoc[]
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

export interface IDutyLocationDoc {
  id: number
  name: string
  workplaceId: number
  companyId: number
  addressLine1: string
  addressLine2: string | null
  addressCity: string
  addressState: string
  stateCode: string
  zipCode: string
  countryCode: string
  country: string
  longitude: number
  latitude: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
}

/** Response for GET /v1/time-attendance/workplaces/:id (single workplace with duty locations & shift plans) */
export type ISetupDutyLocationRes = IBaseRes<ISetupDutyLocation>
export interface ISetupDutyLocation {
  docs?: ISetupDutyLocationDocsEntity[] | null
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}
export interface ISetupDutyLocationDocsEntity {
  id: number
  name: string
  workplaceId: number
  companyId: number
  addressLine1: string
  addressLine2?: null
  addressCity: string
  addressState: string
  stateCode: string
  zipCode: string
  countryCode: string
  country: string
  longitude: number
  latitude: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
}

export type IWorkplaceByIdRes = IBaseRes<IWorkplaceDetailDoc>

export interface IWorkplaceShiftPlanSummaryDoc {
  id: number
  title: string
  type: string
  workplaceId: number
  dutylocationId: number | null
  allowManualTimeEntry: boolean
  requireApprovalForManualTimeEntry: boolean
  allowedClockInMinutesBeforeShift: number
  allowOvertime: boolean
  linkHardwareDevice: boolean
  hardwareSetupContactEmail: string
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  useInPayrollCalculation: boolean
  allowSwapping: boolean
  requireApprovalForSwap: boolean
  allowLateClockIn: boolean
  allowedLateClockinInMinutes: number
  addOvertimeToPayroll: boolean
  startDate: string
  endDate: string
  status: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  companyId: number
  createdByName: string
  updatedByName: string
  hardwareDeviceTypes: HardwareDevices[] | null
}

export interface IWorkplaceDetailDoc {
  id: number
  addressLine1: string
  addressLine2: string | null
  addressCity: string
  addressState: string
  stateCode: string
  zipCode: string
  isPrimary: boolean | null
  phone: string
  displayName: string
  taxOfficeName: string | null
  taxOfficeCode: string | null
  taxId: string | null
  isDeleted: boolean
  isActive: boolean
  country: string
  countryCode: string
  companyId: number
  housingLocationName: string | null
  housingLocationCode: string | null
  fullAddress: string
  hasChildren: boolean
  longitude: number | null | undefined
  latitude: number | null | undefined
  officeDutyLocations: IDutyLocationDoc[]
  shiftPlans: IWorkplaceShiftPlanSummaryDoc[]
  timeSheets: unknown[]
  workplaceConfig?: IWorkplaceConfig
  employees: unknown[]
  city?: string
  state?: string
  nhfOfficeName?: string
  nhfOfficeCode?: string
}

// In ../typings.ts — add this
export interface WorkplaceFormValues {
  workplaceName?: string
  primaryAddress?: string
  latitude?: number | string
  longitude?: number | string
  radius?: number
  city?: string
  country?: string
  state?: string
  taxOffice?: string
  taxOfficeCode?: string
  nhfOffice?: string
  nhfOfficeCode?: string
}

/** Response for GET /v1/time-attendance/shift-plans (paginated) */
export type IShiftPlansRes = IBaseRes<IShiftPlansData>

export interface IShiftPlansData {
  docs: IShiftPlanDoc[]
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

export type ShiftPlanType = "rostered" | "flexible"

export interface IShiftPlanDoc {
  id: number
  title: string
  type: string
  workplaceId: number
  dutylocationId: number
  allowManualTimeEntry: boolean
  requireApprovalForManualTimeEntry: boolean
  allowedClockInMinutesBeforeShift: number
  allowOvertime: boolean
  linkHardwareDevice: boolean
  hardwareSetupContactEmail: string
  hardwareDeviceTypes: (string | HardwareDevices)[]
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  useInPayrollCalculation: boolean
  allowSwapping: boolean
  requireApprovalForSwap: boolean
  allowLateClockIn: boolean
  allowedLateClockinInMinutes: number
  addOvertimeToPayroll: boolean
  startDate: string
  endDate: string
  status: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  companyId: number
  createdByName: string
  updatedByName: string
  totalEmployees: number
  totalShiftPeriods: number
  shiftPeriods: IShiftPeriodDoc[]
  shiftAssignments: IShiftAssignmentDoc[]
  useOvertimeRecord?: boolean
  notificationCadence?: string
  notifiyMeBefore?: boolean
  smsNotification?: boolean
  flaggedForDeletion?: boolean
}

export interface HardwareDevices {
  id: number
  shiftPlanId: number
  deviceType: string
  companyId: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
}

/** Response for GET /v1/time-attendance/shift-periods (paginated) */
export type IShiftPeriodsRes = IBaseRes<IShiftPeriodsData>

export interface IShiftPeriodsData {
  docs: IShiftPeriodDoc[]
  page: number | string
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

export interface IShiftPeriodDoc {
  id: number
  title: string
  startTime: string
  endTime: string
  shiftPlanId: number
  status: string
  assignedEmployeeCount: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  companyId: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  /** Shift differentials applied to this period (optional). */
  shiftDifferentials?: IShiftDifferentialDoc[]
}

// ─── Shift differentials ──────────────────────────────────────────────────────

export type IShiftDifferentialsRes = IBaseRes<IShiftDifferentialsData>

export interface IShiftDifferentialsData {
  docs: IShiftDifferentialDoc[]
  page: number | string
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

export interface IShiftDifferentialDoc {
  id: number
  name: string
  description?: string
  percentage: number
  companyId?: number
  createdDate?: string
  createdBy?: string
  updatedDate?: string
  updatedBy?: string
  recordGuid?: string
  recordVersion?: number
  isDeleted?: boolean
  isActive?: boolean
  createdByName?: string
  updatedByName?: string
  shiftDifferentialsCount?: number
}

export interface IShiftAssignmentEmployee {
  id: number
  personTypeId: number
  managerId: number | null
  employmentStatusId: number
  officeAddressId: number | null
  employeeTypeId: number
  employeeTitleId: number | null
  paygradeId: number | null
  departmentId: number | null
  identificationNumber: string | null
  hireDate: string | null
  firstName: string
  middleInitial: string | null
  lastName: string
  genderId: number | null
  dateOfBirth: string | null
  email: string
  recoveryEmail: string | null
  mobilePhone: string | null
  nin: string | null
  tin: string | null
  nhfEpn: string | null
  paymentMethodId: number | null
  personStatusId: number
  customFields: unknown
  displayPhoneNumber: boolean | null
  welcomeNote: string | null
  sendEnrollmentReminder: boolean | null
  sendDigitalCard: boolean | null
  isInvited: boolean | null
  isOnboarded: boolean
  isOnboardingCancelled: boolean | null
  isNew: boolean
  offerLastSentDate: string | null
  isProfilePhotoUploaded: boolean | null
  uniqueIdentifier: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  reports: number
  peers: number
  isOnProbation: boolean | null
  contractMonths: number | null
  exitDate: string | null
  contractExpiryDate: string | null
  birthdayCardLastSentDate: string | null
  anniversaryCardLastSentDate: string | null
  welcomeCardSent: boolean | null
  companyId: number
  ewaId: string | null
  ewaEnabled: boolean
  preferredAccountClassId: number | null
  isConfirmed: boolean | null
  confirmationDate: string | null
  isSynchronized: boolean
  isRehired: boolean | null
  rehireDate: string | null
  isContactSynchronized: boolean
  walletId: string | null
  walletEnabled: boolean | null
  maritalStatusId: number | null
  workingDays: unknown
  isReinstated: boolean | null
  reinstatementDate: string | null
  suspensionDate: string | null
  isWalletPrimaryAccount: boolean | null
  isWalletSecondaryAccount: boolean | null
  fullName: string
  nickName: string
  title: string | null
  gratuityPin: string | null
  integrationUniqueId: string | null
  offerStatusId: number | null
  setupMessage: string | null
  setupPercentage: number | null
  lastCompensationUpdateDate: string | null
  compensationEffectiveDate: string | null
  assignmentCategory: string | null
  cooperative: string | null
  payrollStartDate: string | null
  avatarFileUrl: string | null
  accomodationType: string | null
  unionMembership: string | null
  teamName: string | null
}

export interface IShiftAssignmentDoc {
  id: number
  employeeId: number
  shiftPlanId: number
  shiftPeriodId: number
  workplaceId: number
  dutyLocationId: number
  dayOfWeek: string
  date: string
  isAnyDayPermitted: boolean
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  companyId: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  employee: IShiftAssignmentEmployee
}

/** Doc shape from GET /v1/time-attendance/shift-plans/:id/shift-assignments */
export interface IShiftAssignmentListDoc {
  id: number
  employeeId: number
  shiftPlanId: number
  shiftPeriodId: number
  dayOfWeek: string
  [key: string]: unknown
}

export interface IShiftAssignmentsResData {
  docs: IShiftAssignmentListDoc[]
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

export type IShiftAssignmentsRes = IBaseRes<IShiftAssignmentsResData>

// ── Shift tables (POST create, GET rostered/any-day assignments) ─────────────

/** Body for POST /v1/time-attendance/shift-plans/:id/shift-tables */
export interface CreateShiftTableBody {
  dutyLocationId?: number | string
  startDate: string
  endDate: string
}

/** Single shift table from API (create response or doc in list) */
export interface IShiftTableDoc {
  id: number
  startDate: string
  endDate: string
  shiftPlanId: number
  dutyLocationId: number
  requiredShiftsPerWeek?: number | null
  companyId?: number
  [key: string]: unknown
}

/** Rostered assignment doc (day array from API) */
export interface IRosteredAssignmentDayDoc {
  id: number
  employeeId: number
  shiftPlanId: number
  shiftPeriodId: number
  shiftTableId: number
  dayOfWeek: string
  [key: string]: unknown
}

/** One shift table doc from GET .../shift-tables/rostered-assignments */
export interface IRosteredAssignmentsTableDoc {
  id: number
  startDate: string
  endDate: string
  shiftPlanId: number
  dutyLocationId: number
  assignments: {
    shiftPeriodId: number
    days: {
      monday?: IRosteredAssignmentDayDoc[]
      tuesday?: IRosteredAssignmentDayDoc[]
      wednesday?: IRosteredAssignmentDayDoc[]
      thursday?: IRosteredAssignmentDayDoc[]
      friday?: IRosteredAssignmentDayDoc[]
      saturday?: IRosteredAssignmentDayDoc[]
      sunday?: IRosteredAssignmentDayDoc[]
    }
  }[]
}

export interface IRosteredAssignmentsResData {
  docs: IRosteredAssignmentsTableDoc[]
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

export type IRosteredAssignmentsRes = IBaseRes<IRosteredAssignmentsResData>

/** Body for POST .../shift-tables/shift-assignments/rostered/bulk */
export interface RosteredBulkAssignmentDay {
  employeeIds: number[]
}
export interface RosteredBulkAssignmentPeriod {
  shiftPeriodId: number
  days: {
    monday?: RosteredBulkAssignmentDay
    tuesday?: RosteredBulkAssignmentDay
    wednesday?: RosteredBulkAssignmentDay
    thursday?: RosteredBulkAssignmentDay
    friday?: RosteredBulkAssignmentDay
    saturday?: RosteredBulkAssignmentDay
    sunday?: RosteredBulkAssignmentDay
  }
}
export interface RosteredBulkAssignmentTable {
  shiftTableId: number
  assignments: RosteredBulkAssignmentPeriod[]
}
export interface RosteredBulkBody {
  dutyLocationId: number | null
  data: RosteredBulkAssignmentTable[]
  removeAssignments?: number[]
}

/** Any-day assignment doc from API */
export interface IAnyDayAssignmentDoc {
  id: number
  employeeId: number
  shiftPlanId: number
  shiftTableId: number
  isAnyDayPermitted: boolean
  weeklyLimit?: number
  employee?: { id: number; fullName?: string; [key: string]: unknown }
  anydayEligibileShifts?: {
    id: number
    shiftPeriodId: number
    shiftPeriod?: { id: number; title: string; [key: string]: unknown }
  }[]
  [key: string]: unknown
}

/** One shift table doc from GET .../shift-tables/any-day-assignments */
export interface IAnyDayAssignmentsTableDoc {
  id: number
  startDate: string
  endDate: string
  shiftPlanId: number
  dutyLocationId: number
  assignments: IAnyDayAssignmentDoc[]
}

export interface IAnyDayAssignmentsResData {
  docs: IAnyDayAssignmentsTableDoc[]
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

export type IAnyDayAssignmentsRes = IBaseRes<IAnyDayAssignmentsResData>

/** Body for POST .../shift-tables/shift-assignments/any-day/bulk */
export interface AnyDayBulkAssignmentItem {
  employeeId: number
  shiftPeriodIds: number[]
  shiftRequiredPerWeek: number
}
export interface AnyDayBulkAssignmentTable {
  shiftTableId: number
  assignments: AnyDayBulkAssignmentItem[]
}
export interface AnyDayBulkBody {
  dutyLocationId?: number | null
  data: AnyDayBulkAssignmentTable[]
  removeAssignments?: number[]
}

/** Payload for POST /v1/time-attendance/shift-plans */
export interface CreateShiftPlanBody {
  title: string
  type: ShiftPlanType
  status: "draft" | "published"
  workplaceId: string
  dutyLocationId: number | null
  allowManualTimeEntry: boolean
  requireApprovalForManualTimeEntry: boolean
  allowedClockInMinutesBeforeShift: number
  allowOvertime: boolean
  linkHardwareDevice: boolean
  hardwareSetupContactEmail: string
  hardwareDeviceTypes: HardwareDevices[]
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  useInPayrollCalculation: boolean
  allowSwapping: boolean
  requireApprovalForSwap: boolean
  allowLateClockIn: boolean
  allowedLateClockinInMinutes: number
  addOvertimeToPayroll: boolean
  startDate: string
  endDate: string
  notificationCadence: string
  notifiyMeBefore: boolean
  smsNotification: boolean
  useOvertimeRecord: boolean
  notifyMeBeforeSmsSends: boolean
}

export type IAnalyticsRes = IBaseRes<IAnalytics>
export interface IAnalytics {
  period: string
  currentPeriod: IAnalyticsCurrentPeriodOrPreviousPeriod
  previousPeriod: IAnalyticsCurrentPeriodOrPreviousPeriod
  metrics: IAnalyticsMetrics
  counters: IAnalyticsCounters
}
export interface IAnalyticsCurrentPeriodOrPreviousPeriod {
  start: string
  end: string
}
export interface IAnalyticsMetrics {
  shiftAdherence: IAnalyticsShiftAdherenceOrAbsenteeismRateOrTimeAdherenceOrOvertimeSubmissions
  absenteeismRate: IAnalyticsShiftAdherenceOrAbsenteeismRateOrTimeAdherenceOrOvertimeSubmissions
  timeAdherence: IAnalyticsShiftAdherenceOrAbsenteeismRateOrTimeAdherenceOrOvertimeSubmissions
  overtimeSubmissions: IAnalyticsShiftAdherenceOrAbsenteeismRateOrTimeAdherenceOrOvertimeSubmissions
}
export interface IAnalyticsShiftAdherenceOrAbsenteeismRateOrTimeAdherenceOrOvertimeSubmissions {
  value: number
  previousValue: number
  delta: number
  comparable: boolean
}
export interface IAnalyticsCounters {
  current: IAnalyticsCurrentOrPrevious
  previous: IAnalyticsCurrentOrPrevious
}
export interface IAnalyticsCurrentOrPrevious {
  totalScheduledShifts: number
  properlyAttendedShifts: number
  missedShifts: number
  totalAttendedShifts: number
  fullDurationShifts: number
  totalEligibleEmployees: number
  overtimeEmployees: number
}

export type ITimeSheetsRes = IBaseRes<ITimeSheets>
export interface ITimeSheets {
  docs?: ITimeSheetsDocsEntity[] | null
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}
export interface ITimeSheetsDocsEntity {
  id: number
  companyId: number
  employeeId: number
  shiftPlanId: number
  shiftPeriodId: number
  shiftAssignmentId: number
  clockIn: string
  clockOut: string
  localDayOfWeek: string
  method?: null
  reason?: null
  origin: string
  geofenceStatus: string
  counted: boolean
  approvalStatus: string
  createdDate: string
  createdBy?: null
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName?: null
  updatedByName: string
  officeAddressId: number
  employee: ITimeSheetsEmployee
  shiftPlan: ITimeSheetsShiftPlan
  shiftPeriod: ITimeSheetsShiftPeriod
  shiftAssignment: ITimeSheetsShiftAssignment
}
export interface ITimeSheetsEmployee {
  id: number
  personTypeId: number
  managerId?: number | null
  employmentStatusId: number
  officeAddressId?: null
  employeeTypeId: number
  employeeTitleId?: number | null
  paygradeId?: number | null
  departmentId: number
  identificationNumber: string
  hireDate?: string | null
  firstName: string
  middleInitial: string
  lastName: string
  genderId?: null
  dateOfBirth?: string | null
  email: string
  recoveryEmail?: string | null
  mobilePhone?: string | null
  nin?: string | null
  tin?: string | null
  nhfEpn?: string | null
  paymentMethodId: number
  personStatusId: number
  customFields?: null
  displayPhoneNumber?: boolean | null
  welcomeNote?: string | null
  sendEnrollmentReminder?: boolean | null
  sendDigitalCard?: boolean | null
  isInvited?: null
  isOnboarded: boolean
  isOnboardingCancelled?: null
  isNew: boolean
  offerLastSentDate?: null
  isProfilePhotoUploaded?: null
  uniqueIdentifier: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  reports: number
  peers: number
  isOnProbation?: null
  contractMonths?: null
  exitDate?: null
  contractExpiryDate?: null
  birthdayCardLastSentDate?: null
  anniversaryCardLastSentDate?: null
  welcomeCardSent?: null
  companyId: number
  ewaId?: string | null
  ewaEnabled?: boolean | null
  preferredAccountClassId: number
  isConfirmed?: null
  confirmationDate?: null
  isSynchronized: boolean
  isRehired?: null
  rehireDate?: null
  isContactSynchronized: boolean
  walletId?: null
  walletEnabled?: null
  maritalStatusId?: null
  workingDays?: null
  isReinstated?: null
  reinstatementDate?: null
  suspensionDate?: string | null
  isWalletPrimaryAccount?: null
  isWalletSecondaryAccount?: null
  fullName: string
  nickName: string
  title?: string | null
  gratuityPin?: null
  integrationUniqueId?: string | null
  offerStatusId?: null
  setupMessage: string
  setupPercentage: number
  lastCompensationUpdateDate?: null
  compensationEffectiveDate?: null
  assignmentCategory?: null
  cooperative?: null
  payrollStartDate?: null
  avatarFileUrl?: null
  accomodationType?: null
  unionMembership?: string | null
  teamName?: null
}
export interface ITimeSheetsShiftPlan {
  id: number
  title: string
  type: string
  workplaceId: number
  dutylocationId?: null
  allowManualTimeEntry: boolean
  requireApprovalForManualTimeEntry: boolean
  allowedClockInMinutesBeforeShift: number
  allowOvertime: boolean
  linkHardwareDevice: boolean
  hardwareSetupContactEmail: string
  hardwareDevices?: HardwareDevices[] | null
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  useInPayrollCalculation: boolean
  allowSwapping: boolean
  requireApprovalForSwap: boolean
  allowLateClockIn: boolean
  allowedLateClockinInMinutes?: null
  addOvertimeToPayroll: boolean
  startDate?: null
  endDate?: null
  status: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  companyId: number
  createdByName: string
  updatedByName: string
}
export interface ITimeSheetsShiftPeriod {
  id: number
  title: string
  startTime: string
  endTime: string
  shiftPlanId: number
  status: string
  assignedEmployeeCount: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  companyId: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
}
export interface ITimeSheetsShiftAssignment {
  id: number
  employeeId: number
  shiftPlanId: number
  shiftPeriodId: number
  workplaceId?: null
  dutyLocationId?: null
  dayOfWeek: string
  date?: null
  isAnyDayPermitted: boolean
  createdDate: string
  createdBy?: null
  updatedDate: string
  updatedBy?: null
  recordGuid?: string | null
  recordVersion: number
  companyId?: number | null
  isDeleted: boolean
  isActive: boolean
  createdByName?: null
  updatedByName?: null
}

export type IWorkPlacesRes = IBaseRes<IWorkPlaces>
export interface IWorkPlaces {
  docs?: IWorkPlacesEntity[] | null
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}
export interface IWorkPlacesEntity {
  id: number
  addressLine1: string
  addressLine2?: string | null
  addressCity: string
  addressState: string
  stateCode: string
  zipCode: string
  isPrimary?: null
  phone: string
  displayName: string
  taxOfficeName?: string | null
  taxOfficeCode?: string | null
  taxId?: string | null
  isDeleted: boolean
  isActive: boolean
  country: string
  countryCode: string
  companyId: number
  housingLocationName?: string | null
  housingLocationCode?: string | null
  fullAddress: string
  hasChildren: boolean
  longitude?: string | null
  latitude?: string | null
  officeDutyLocations?: (OfficeDutyLocationsEntity | null)[] | null
  shiftPlans?: (ShiftPlansEntity | null)[] | null
  timeSheets?: null[] | null
  workplaceConfig?: WorkplaceConfig | null
  employees?: (IWorkPlacesEmployeesEntity | null)[] | null
  recordStatus?: number | null
}

export interface EmployeeOfficeDutyLocationsEntity {
  docs?: OfficeDutyLocationsEntity[] | null
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}

export interface OfficeDutyLocationsEntity {
  id: number
  name: string
  workplaceId: number
  companyId: number
  addressLine1: string
  addressLine2?: string | null
  addressCity: string
  addressState: string
  stateCode: string
  zipCode: string
  countryCode: string
  country: string
  longitude: number
  latitude: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
}
export interface ShiftPlansEntity {
  id: number
  title: string
  type: string
  workplaceId: number
  dutylocationId?: number | null
  allowManualTimeEntry: boolean
  requireApprovalForManualTimeEntry: boolean
  allowedClockInMinutesBeforeShift: number
  allowOvertime: boolean
  linkHardwareDevice: boolean
  hardwareSetupContactEmail: string
  hardwareDevices?: HardwareDevices[] | null
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  useInPayrollCalculation: boolean
  allowSwapping: boolean
  requireApprovalForSwap: boolean
  allowLateClockIn: boolean
  allowedLateClockinInMinutes?: number | null
  addOvertimeToPayroll: boolean
  startDate?: string | null
  endDate?: string | null
  status: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  companyId?: number | null
  createdByName: string
  updatedByName: string
}
export interface WorkplaceConfig {
  id: number
  companyId: number
  workplaceId: number
  isActive: boolean
  status: string
  configDone: boolean
  workplaceSelected: boolean
  shiftPlanConfigured: boolean
  shiftPermissionsConfigured: boolean
  shiftPeriodConfigured: boolean
  shiftEmployeesConfigured: boolean
  shiftPlansConfirmed: boolean
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  createdByName: string
  updatedByName: string
}
export interface IWorkPlacesEmployeesEntity {
  id: number
  personTypeId: number
  managerId?: number | null
  employmentStatusId: number
  officeAddressId: number
  employeeTypeId: number
  employeeTitleId?: number | null
  paygradeId: number
  departmentId: number
  identificationNumber: string
  hireDate: string
  firstName: string
  middleInitial: string
  lastName: string
  genderId?: number | null
  dateOfBirth: string
  email: string
  recoveryEmail?: string | null
  mobilePhone: string
  nin?: string | null
  tin: string
  nhfEpn?: string | null
  paymentMethodId: number
  personStatusId: number
  customFields?: null
  displayPhoneNumber: boolean
  welcomeNote?: string | null
  sendEnrollmentReminder?: boolean | null
  sendDigitalCard?: boolean | null
  isInvited?: boolean | null
  isOnboarded: boolean
  isOnboardingCancelled?: null
  isNew: boolean
  offerLastSentDate?: null
  isProfilePhotoUploaded?: boolean | null
  uniqueIdentifier: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  reports: number
  peers: number
  isOnProbation?: null
  contractMonths?: null
  exitDate?: null
  contractExpiryDate?: null
  birthdayCardLastSentDate?: string | null
  anniversaryCardLastSentDate?: null
  welcomeCardSent?: null
  companyId: number
  ewaId?: string | null
  ewaEnabled?: boolean | null
  preferredAccountClassId: number
  isConfirmed?: boolean | null
  confirmationDate?: string | null
  isSynchronized?: boolean | null
  isRehired?: boolean | null
  rehireDate?: string | null
  isContactSynchronized: boolean
  walletId: string
  walletEnabled: boolean
  maritalStatusId?: number | null
  workingDays?: null
  isReinstated?: null
  reinstatementDate?: null
  suspensionDate?: null
  isWalletPrimaryAccount?: boolean | null
  isWalletSecondaryAccount?: null
  fullName: string
  nickName: string
  title?: null
  gratuityPin?: null
  integrationUniqueId?: null
  offerStatusId?: null
  setupMessage: string
  setupPercentage: number
  lastCompensationUpdateDate?: null
  compensationEffectiveDate?: null
  assignmentCategory?: null
  cooperative?: null
  payrollStartDate?: null
  avatarFileUrl?: string | null
  accomodationType?: null
  unionMembership?: null
  teamName?: null
}

export interface WorkplaceConfig {
  id: number
  companyId: number
  workplaceId: number
  isActive: boolean
  status: string
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  configDone: boolean
  workplaceSelected: boolean
  shiftPlanConfigured: boolean
  shiftPermissionsConfigured: boolean
  shiftPeriodConfigured: boolean
  shiftEmployeesConfigured: boolean
  shiftPlansConfirmed: boolean
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  createdByName: string
  updatedByName: string
}

export type IWorkplaceDepartmentRes = IBaseRes<IWorkplaceDepartment>
export interface IWorkplaceDepartment {
  docs?: IWorkplaceDepartmentDocsEntity[] | null
  page: string
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: string
  previousPage: number
  limit: number
  totalDocs: number
  totalPages: number
}
export interface IWorkplaceDepartmentDocsEntity {
  id: number
  name: string
  createdDate: string
}

export type ITimeSheetRes = IBaseRes<ITimeSheetPagi>
export interface ITimeSheetPagi {
  docs?: Record<string, ITimeSheetDocsEntity[]> | null
  groupBy?: string
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}
export interface ITimeSheetDocsEntity {
  id: number
  companyId: number
  employeeId: number
  shiftPlanId: number
  shiftPeriodId: number
  shiftAssignmentId: number
  workplaceId?: null
  dutyLocationId?: null
  clockIn: string
  clockOut: string
  localDayOfWeek: string
  method: string
  reason?: null
  origin: string
  geofenceStatus: string
  counted: boolean
  approvalStatus: string
  lateClockin: boolean
  earlyClockin: boolean
  earlyClockout: boolean
  lateClockout: boolean
  overtime: boolean
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  approvalReason?: string | null
  rejectionReason: null | string
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  officeAddressId: number
  employee: ITimeSheetDocsEntityEmployee
  shiftPlan?: IShiftPlanDoc
  shiftPeriod?: IShiftPeriodDoc
  shiftAssignment?: IShiftAssignmentDoc
  nextReviewEntityId: null
  nextReviewEntityRoleId: number
  nextReviewEntityRoleName: string
  reviewStatus: string
  approvalStatusId: number
  reviewDate: string | null
  note: string | null
}
export interface ITimeSheetDocsEntityEmployee {
  id: number
  personTypeId: number
  managerId?: null
  employmentStatusId: number
  officeAddressId: number
  employeeTypeId: number
  employeeTitleId?: null
  paygradeId: number
  departmentId: number
  identificationNumber: string
  hireDate: string
  firstName: string
  middleInitial: string
  lastName: string
  genderId?: null
  dateOfBirth?: null
  department: ITimeSheetDocsEntityIDepartment
  email?: null
  recoveryEmail?: null
  mobilePhone?: null
  nin?: null
  tin: string
  nhfEpn: string
  paymentMethodId: number
  personStatusId: number
  customFields?: null
  displayPhoneNumber?: null
  welcomeNote?: null
  sendEnrollmentReminder?: null
  sendDigitalCard?: null
  isInvited?: null
  isOnboarded: boolean
  isOnboardingCancelled?: null
  isNew: boolean
  offerLastSentDate?: null
  isProfilePhotoUploaded?: null
  uniqueIdentifier: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  reports: number
  peers: number
  isOnProbation?: null
  contractMonths?: null
  exitDate?: null
  contractExpiryDate?: null
  birthdayCardLastSentDate?: null
  anniversaryCardLastSentDate?: null
  welcomeCardSent?: null
  companyId: number
  ewaId: string
  ewaEnabled: boolean
  preferredAccountClassId: number
  isConfirmed?: null
  confirmationDate?: null
  isSynchronized: boolean
  isRehired?: null
  rehireDate?: null
  isContactSynchronized?: null
  walletId?: null
  walletEnabled?: null
  maritalStatusId?: null
  workingDays?: null
  isReinstated?: null
  reinstatementDate?: null
  suspensionDate?: null
  isWalletPrimaryAccount?: null
  isWalletSecondaryAccount?: null
  fullName: string
  nickName: string
  title?: null
  gratuityPin?: null
  integrationUniqueId?: null
  offerStatusId?: null
  setupMessage: string
  setupPercentage: number
  lastCompensationUpdateDate?: null
  compensationEffectiveDate?: null
  assignmentCategory?: null
  cooperative?: null
  payrollStartDate?: null
  avatarFileUrl?: null
  accomodationType?: null
  unionMembership?: null
  teamName?: null
}
export interface ITimeSheetDocsEntityIDepartment {
  id: number
  description: string
  personId: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  companyId: number
}

export type IShiftSwapsRes = IBaseRes<IShiftSwaps>
export interface IShiftSwaps {
  docs?: IShiftSwapsDocsEntity[] | null
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}
export interface IShiftSwapsDocsEntity {
  id: number
  sourceShiftAssignmentId: number
  targetShiftAssignmentId: number
  requestedByEmployeeId: number
  targetEmployeeId: number
  status: string
  approvalStatusId: number
  requesterReason: string
  targetEmployeeReason?: string | null
  targetEmployeeApproved: boolean
  reviewComment?: null
  reviewedDate?: string | null
  reviewedBy?: string | null
  reviewedByName?: string | null
  companyId: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  sourceShiftAssignment: IShiftSwapsSourceShiftAssignmentOrTargetShiftAssignment
  targetShiftAssignment: IShiftSwapsSourceShiftAssignmentOrTargetShiftAssignment
  requester: IShiftSwapsRequester
  targetEmployee: IShiftSwapsTargetEmployee
  nextReviewEntityId: number
  nextReviewEntityRoleId: number
  nextReviewEntityRoleName: string
  reviewStatus: string
}
export interface IShiftSwapsSourceShiftAssignmentOrTargetShiftAssignment {
  id: number
  employeeId: number
  shiftPlanId: number
  shiftPeriodId: number
  workplaceId: number
  dutyLocationId: number
  dayOfWeek: string
  date: string
  isAnyDayPermitted: boolean
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  companyId: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  shiftPlan: IShiftSwapsShiftPlan
  shiftPeriod: IShiftSwapsShiftPeriod
}
export interface IShiftSwapsShiftPlan {
  id: number
  title: string
  type: string
  workplaceId: number
  dutylocationId: number
  allowManualTimeEntry: boolean
  requireApprovalForManualTimeEntry: boolean
  allowedClockInMinutesBeforeShift: number
  allowOvertime: boolean
  linkHardwareDevice: boolean
  hardwareSetupContactEmail: string
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  useInPayrollCalculation: boolean
  allowSwapping: boolean
  requireApprovalForSwap: boolean
  allowLateClockIn: boolean
  allowedLateClockinInMinutes: number
  addOvertimeToPayroll: boolean
  startDate: string
  endDate: string
  status: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  companyId: number
  createdByName: string
  updatedByName: string
  hardwareDeviceTypes: HardwareDevices[] | null
}
export interface IShiftSwapsShiftPeriod {
  id: number
  title: string
  startTime: string
  endTime: string
  shiftPlanId: number
  status: string
  assignedEmployeeCount: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  companyId: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
}
export interface IShiftSwapsRequester {
  id: number
  personTypeId: number
  managerId: number
  employmentStatusId: number
  officeAddressId: number
  employeeTypeId: number
  employeeTitleId: number
  paygradeId: number
  departmentId: number
  identificationNumber: string
  hireDate: string
  firstName: string
  middleInitial: string
  lastName: string
  genderId?: null
  dateOfBirth: string
  department: IShiftSwapsTargetEmployeeIDepartment
  email: string
  recoveryEmail: string
  mobilePhone: string
  nin?: null
  tin: string
  nhfEpn?: null
  paymentMethodId: number
  personStatusId: number
  customFields?: null
  displayPhoneNumber: boolean
  welcomeNote?: null
  sendEnrollmentReminder: boolean
  sendDigitalCard: boolean
  isInvited: boolean
  isOnboarded: boolean
  isOnboardingCancelled?: null
  isNew: boolean
  offerLastSentDate?: null
  isProfilePhotoUploaded: boolean
  uniqueIdentifier: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  reports: number
  peers: number
  isOnProbation?: null
  contractMonths?: null
  exitDate?: null
  contractExpiryDate?: null
  birthdayCardLastSentDate?: null
  anniversaryCardLastSentDate?: null
  welcomeCardSent?: null
  companyId: number
  ewaId?: null
  ewaEnabled?: null
  preferredAccountClassId: number
  isConfirmed?: null
  confirmationDate?: null
  isSynchronized?: null
  isRehired?: null
  rehireDate?: null
  isContactSynchronized: boolean
  walletId: string
  walletEnabled: boolean
  maritalStatusId: number
  workingDays?: null
  isReinstated?: null
  reinstatementDate?: null
  suspensionDate?: null
  isWalletPrimaryAccount?: null
  isWalletSecondaryAccount?: null
  fullName: string
  nickName: string
  title?: null
  gratuityPin?: null
  integrationUniqueId?: null
  offerStatusId?: null
  setupMessage: string
  setupPercentage: number
  lastCompensationUpdateDate?: null
  compensationEffectiveDate?: null
  assignmentCategory?: null
  cooperative?: null
  payrollStartDate?: null
  avatarFileUrl: string
  accomodationType?: null
  unionMembership?: null
  teamName: string
}
export interface IShiftSwapsTargetEmployeeIDepartment {
  id: number
  description: string
  personId: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  companyId: number
}
export interface IShiftSwapsTargetEmployee {
  id: number
  personTypeId: number
  managerId?: null
  employmentStatusId: number
  officeAddressId: number
  employeeTypeId: number
  employeeTitleId?: null
  paygradeId: number
  departmentId: number
  identificationNumber: string
  hireDate: string
  firstName: string
  middleInitial: string
  lastName: string
  genderId?: null
  dateOfBirth?: null
  department: IShiftSwapsTargetEmployeeIDepartment
  email?: null
  recoveryEmail?: null
  mobilePhone?: null
  nin?: null
  tin: string
  nhfEpn: string
  paymentMethodId: number
  personStatusId: number
  customFields?: null
  displayPhoneNumber?: null
  welcomeNote?: null
  sendEnrollmentReminder?: null
  sendDigitalCard?: null
  isInvited?: null
  isOnboarded: boolean
  isOnboardingCancelled?: null
  isNew: boolean
  offerLastSentDate?: null
  isProfilePhotoUploaded?: null
  uniqueIdentifier: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  reports: number
  peers: number
  isOnProbation?: null
  contractMonths?: null
  exitDate?: null
  contractExpiryDate?: null
  birthdayCardLastSentDate?: null
  anniversaryCardLastSentDate?: null
  welcomeCardSent?: null
  companyId: number
  ewaId: string
  ewaEnabled: boolean
  preferredAccountClassId: number
  isConfirmed?: null
  confirmationDate?: null
  isSynchronized: boolean
  isRehired?: null
  rehireDate?: null
  isContactSynchronized?: null
  walletId?: null
  walletEnabled?: null
  maritalStatusId?: null
  workingDays?: null
  isReinstated?: null
  reinstatementDate?: null
  suspensionDate?: null
  isWalletPrimaryAccount?: null
  isWalletSecondaryAccount?: null
  fullName: string
  nickName: string
  title?: null
  gratuityPin?: null
  integrationUniqueId?: null
  offerStatusId?: null
  setupMessage: string
  setupPercentage: number
  lastCompensationUpdateDate?: null
  compensationEffectiveDate?: null
  assignmentCategory?: null
  cooperative?: null
  payrollStartDate?: null
  avatarFileUrl?: null
  accomodationType?: null
  unionMembership?: null
  teamName?: null
}

export type IViewTimeSheetRes = IBaseRes<IViewTimeSheet>
export interface IViewTimeSheet {
  docs?: IViewTimeSheetDoc[] | null
  employee: IViewTimeSheetEmployee
  department: IViewTimeSheetDepartment
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}
export interface IViewTimeSheetDoc {
  id: number
  title: string
  startTime: string
  endTime: string
  shiftPlanId: number
  status: string
  assignedEmployeeCount: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  companyId: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  shiftPlan: IViewTimeSheetShiftPlan
  shiftAssignments?: IViewTimeSheetShiftAssignmentsEntity[] | null
}
export interface IViewTimeSheetShiftPlan {
  id: number
  title: string
  type: string
  workplaceId: number
  dutylocationId: number
  allowManualTimeEntry: boolean
  requireApprovalForManualTimeEntry: boolean
  allowedClockInMinutesBeforeShift: number
  allowOvertime: boolean
  linkHardwareDevice: boolean
  hardwareSetupContactEmail: string
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  useInPayrollCalculation: boolean
  allowSwapping: boolean
  requireApprovalForSwap: boolean
  allowLateClockIn: boolean
  allowedLateClockinInMinutes: number
  addOvertimeToPayroll: boolean
  startDate: string
  endDate: string
  status: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  companyId: number
  createdByName: string
  updatedByName: string
  hardwareDeviceTypes: HardwareDevices[] | null
}
export interface IViewTimeSheetShiftAssignmentsEntity {
  id: number
  employeeId: number
  shiftPlanId: number
  shiftPeriodId: number
  workplaceId: number
  dutyLocationId: number
  dayOfWeek: string
  date: string
  isAnyDayPermitted: boolean
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  companyId: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
}

export interface IViewTimeSheetEmployee {
  id: number
  personTypeId: number
  managerId: number
  employmentStatusId: number
  officeAddressId: number
  employeeTypeId: number
  employeeTitleId: number
  paygradeId: number
  departmentId: number
  identificationNumber: string
  hireDate: string
  firstName: string
  middleInitial: string
  lastName: string
  genderId?: null
  dateOfBirth: string
  email: string
  recoveryEmail: string
  mobilePhone: string
  nin?: null
  tin: string
  nhfEpn?: null
  paymentMethodId: number
  personStatusId: number
  customFields?: null
  displayPhoneNumber: boolean
  welcomeNote?: null
  sendEnrollmentReminder?: null
  sendDigitalCard?: null
  isInvited: boolean
  isOnboarded: boolean
  isOnboardingCancelled?: null
  isNew: boolean
  offerLastSentDate?: null
  isProfilePhotoUploaded: boolean
  uniqueIdentifier: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  reports: number
  peers: number
  isOnProbation?: null
  contractMonths?: null
  exitDate?: null
  contractExpiryDate?: null
  birthdayCardLastSentDate: string
  anniversaryCardLastSentDate?: null
  welcomeCardSent?: null
  companyId: number
  ewaId: string
  ewaEnabled: boolean
  preferredAccountClassId: number
  isConfirmed?: null
  confirmationDate?: null
  isSynchronized?: null
  isRehired?: null
  rehireDate?: null
  isContactSynchronized: boolean
  walletId: string
  walletEnabled: boolean
  maritalStatusId?: null
  workingDays?: null
  isReinstated?: null
  reinstatementDate?: null
  suspensionDate?: null
  isWalletPrimaryAccount?: null
  isWalletSecondaryAccount?: null
  fullName: string
  nickName: string
  title?: null
  gratuityPin?: null
  integrationUniqueId?: null
  offerStatusId?: null
  setupMessage: string
  setupPercentage: number
  lastCompensationUpdateDate?: null
  compensationEffectiveDate?: null
  assignmentCategory?: null
  cooperative?: null
  payrollStartDate?: null
  avatarFileUrl: string
  accomodationType?: null
  unionMembership?: null
  teamName?: null
}
export interface IViewTimeSheetDepartment {
  id: number
  name: string
}

/** Response for GET /admin/people/select?type=Employees */
export interface IEmployeeSelectDoc {
  person_id: number
  person_key: string
  person_status_id: number
  person_status: string
  offer_status_id: number | null
  person_type: string
  first_name: string
  middle_name: string | null
  last_name: string
  nick_name: string
  full_name: string
  identification_number: string
  gender: string | null
  email: string
  mobile_phone: string | null
  is_current_user: string
  employee_title: string | null
  avatar_url: string | null
  display_phone_number: boolean | null
  payband_id: number | null
  payband: string | null
  paygrade_id: number | null
  paygrade: string | null
  department_id: number
  department: string
  office_display_name: string | null
  is_invited: boolean | null
  is_onboarded: boolean
  is_onboarding_cancelled: boolean | null
  is_active: boolean
  is_deleted: boolean
}

export interface IEmployeeSelectPage {
  docs: IEmployeeSelectDoc[]
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

export interface IEmployeeSelectRes {
  success: boolean
  code: number
  message: string | null
  data: {
    success: boolean
    code: number
    message: string | null
    data: IEmployeeSelectPage
  }
  errors: unknown
}

export type IDutyLocationRes = IBaseRes<IDutyLocation>
export interface IDutyLocation {
  docs?: IDutyLocationDocsEntity[] | null
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}
export interface IDutyLocationDocsEntity {
  id: number
  name: string
  workplaceId: number
  companyId: number
  addressLine1: string
  addressLine2?: null
  addressCity: string
  addressState: string
  stateCode: string
  zipCode: string
  countryCode: string
  country: string
  longitude: number
  latitude: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  workplace: IDutyLocationWorkplace
}
export interface IDutyLocationWorkplace {
  id: number
  addressLine1: string
  addressLine2: string
  addressCity: string
  addressState: string
  stateCode: string
  zipCode: string
  isPrimary?: null
  phone: string
  displayName: string
  taxOfficeName?: null
  taxOfficeCode?: null
  taxId: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  country: string
  countryCode: string
  companyId: number
  housingLocationName?: null
  housingLocationCode?: null
  fullAddress: string
  hasChildren: boolean
  longitude: string
  latitude: string
}

export type ClockInEmployeeRes = IBaseRes<ClockInEmployee>

export interface ClockInEmployee {
  shiftPlanId: number
  employeesClockedIn: number
}

export type IDraftShiftDataRes = IBaseRes<IDraftShiftData>
export interface IDraftShiftData {
  docs?: IDraftShiftDataDocsEntity[] | null
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}
export interface IDraftShiftDataDocsEntity {
  id: number
  title: string
  type: string
  workplaceId: number
  dutylocationId: number
  allowManualTimeEntry: boolean
  requireApprovalForManualTimeEntry: boolean
  allowedClockInMinutesBeforeShift: number
  allowOvertime: boolean
  linkHardwareDevice: boolean
  hardwareSetupContactEmail: string
  allowGeoFencing: boolean
  geofencingOffsetMeters: number
  useInPayrollCalculation: boolean
  allowSwapping: boolean
  requireApprovalForSwap: boolean
  allowLateClockIn: boolean
  allowedLateClockinInMinutes: number
  addOvertimeToPayroll: boolean
  startDate: string
  endDate: string
  status: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  companyId: number
  createdByName: string
  updatedByName: string
  hardwareDeviceTypes: HardwareDevices[]
  totalEmployees: number
  totalShiftPeriods: number
  useOvertimeRecord?: boolean
  notificationCadence?: string
  notifiyMeBefore?: string
  smsNotification?: boolean
}

export type ITimeSheetUploadRes = IBaseRes<ITimeSheetUpload>
export interface ITimeSheetUpload {
  total: number
  createdCount: number
  failedCount: number
  created?: null[] | null
  failed?: ITimeSheetUploadFailedEntity[] | null
}
export interface ITimeSheetUploadFailedEntity {
  index: number
  employeeId: number
  reason: string
}

export type IEnrolledEmployeeRes = IBaseRes<IEnrolledEmployee>
export interface IEnrolledEmployee {
  docs?: IEnrolledEmployeeDocsEntity[] | null
  page: number
  limit: number
  totalDocs: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
}
export interface IEnrolledEmployeeDocsEntity {
  id: number
  firstName: string
  lastName: string
  email: string
  identificationNumber: string
  officeAddressId: number
  department: IEnrolledEmployeeDepartment
  companyId: number
  isActive: boolean
  currentShiftPlan: IEnrolledEmployeeCurrentShiftPlan
  totalShiftsAssignedThisWeek: number
  totalClockInsThisWeek: number
  totalShiftsAssignedAllTime: number
  totalShiftsMissed: number
  totalEarlyClockOuts: number
  totalClockIns: number
}
export interface IEnrolledEmployeeDepartment {
  id: number
  name: string
}
export interface IEnrolledEmployeeCurrentShiftPlan {
  id: number
  title: string
  type: string
  status: string
  startDate: string
  endDate: string
  workplaceId: number
}

export type IOvertimeRes = IBaseRes<IOvertime>
export interface IOvertime {
  docs?: IOvertimeDocsEntity[] | null
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
  limit: number
  totalDocs: number
  totalPages: number
}
export interface IOvertimeDocsEntity {
  departmentId: number
  departmentName: string
  records?: IOvertimeRecordsEntity[] | null
}
export interface IOvertimeRecordsEntity {
  id: number
  departmentId: number
  timesheetId?: null
  employeeId: number
  date: string
  overtimeStartTime: string
  overtimeEndTime: string
  reason: string
  status: string
  approvalStatusId: number
  approvedBy?: string | null
  approvedDate?: string | null
  rejectedBy?: string | null
  rejectedDate?: string | null
  approvalReason?: string | null
  rejectionReason?: string | null
  companyId: number
  createdDate: string
  createdBy?: null
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName?: null
  updatedByName: string
  department: Department
  employee: IOvertimeEmployee
  timesheet?: null
  reviewDate: string | null
  note: string | null
}
export interface Department {
  id: number
  description: string
  personId: number
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  companyId: number
}
export interface IOvertimeEmployee {
  id: number
  personTypeId: number
  managerId: number
  employmentStatusId: number
  officeAddressId: number
  employeeTypeId: number
  employeeTitleId: number
  paygradeId: number
  departmentId: number
  identificationNumber: string
  hireDate: string
  firstName: string
  middleInitial: string
  lastName: string
  genderId?: null
  dateOfBirth: string
  email: string
  recoveryEmail: string
  mobilePhone: string
  nin?: null
  tin: string
  nhfEpn?: null
  paymentMethodId: number
  personStatusId: number
  customFields?: null
  displayPhoneNumber: boolean
  welcomeNote?: null
  sendEnrollmentReminder: boolean
  sendDigitalCard: boolean
  isInvited: boolean
  isOnboarded: boolean
  isOnboardingCancelled?: null
  isNew: boolean
  offerLastSentDate?: null
  isProfilePhotoUploaded: boolean
  uniqueIdentifier: string
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  reports: number
  peers: number
  isOnProbation?: null
  contractMonths?: null
  exitDate?: null
  contractExpiryDate?: null
  birthdayCardLastSentDate?: null
  anniversaryCardLastSentDate?: null
  welcomeCardSent?: null
  companyId: number
  ewaId?: null
  ewaEnabled?: null
  preferredAccountClassId: number
  isConfirmed?: null
  confirmationDate?: null
  isSynchronized?: null
  isRehired?: null
  rehireDate?: null
  isContactSynchronized: boolean
  walletId: string
  walletEnabled: boolean
  maritalStatusId: number
  workingDays?: null
  isReinstated?: null
  reinstatementDate?: null
  suspensionDate?: null
  isWalletPrimaryAccount?: null
  isWalletSecondaryAccount?: null
  fullName: string
  nickName: string
  title?: null
  gratuityPin?: null
  integrationUniqueId?: null
  offerStatusId?: null
  setupMessage: string
  setupPercentage: number
  lastCompensationUpdateDate?: null
  compensationEffectiveDate?: null
  assignmentCategory?: null
  cooperative?: null
  payrollStartDate?: null
  avatarFileUrl: string
  accomodationType?: null
  unionMembership?: null
  teamName: string
}

export type IEmployeeAssignmentRes = IBaseRes<IEmployeeAssignmentData>
export interface IEmployeeAssignmentData {
  docs?: IEmployeeAssignmentDocsEntity[] | null
  page: number
  limit: number
  totalDocs: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage?: null
  previousPage?: null
}
export interface IEmployeeAssignmentDocsEntity {
  id: number
  firstName: string
  lastName: string
  email: string
  identificationNumber: string
  officeAddressId: number
  department: IEmployeeAssignmentDepartment
  companyId: number
  isActive: boolean
  currentShiftPlan: IEmployeeAssignmentCurrentShiftPlan
  totalShiftsAssignedThisWeek: number
  totalClockInsThisWeek: number
  totalShiftsAssignedAllTime: number
  totalShiftsMissed: number
  totalEarlyClockOuts: number
  totalClockIns: number
}
export interface IEmployeeAssignmentDepartment {
  id: number
  name: string
}
export interface IEmployeeAssignmentCurrentShiftPlan {
  id: number
  title: string
  type: string
  status: string
  startDate: string
  endDate: string
  workplaceId: number
}

export interface TimeSheetReview {
  success: boolean
  code: number
  message: string
  data?: TimeSheetReviewDataEntity[] | null
}
export interface TimeSheetReviewDataEntity {
  id: number
  timeSheetId: number
  personId: number
  reviewActionId: number
  message: string
  comment?: null
  createdDate: string
  createdBy: string
  updatedDate: string
  updatedBy: string
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName: string
  updatedByName: string
  companyId: number
}
